/**
 * What moving a run will do to the holds it already has, in one sentence.
 *
 * Pure and dependency-free (the only import is a type, which is erased) so it
 * can be unit tested: vitest here is node-only and matches `src/**\/*.test.ts`,
 * so wording left inline in the dialog's `.tsx` would never be covered.
 *
 * The distinction it exists to make: equipment and material are released and
 * re-taken automatically when the schedule moves, but a unit an operator has
 * already STARTED at the bench is left exactly where it is. Promising to move
 * that would be untrue, and it is the one case where the run's new date and its
 * booked date legitimately disagree.
 */
import type { ProcedureHolds } from '@/api/proceduresApi'

export function moveImpactText(holds: ProcedureHolds, clearing: boolean): string {
  // `startedUnits` is a SUBSET of `equipmentUnits`, so the movable count is the
  // difference — counting both would double-report the same machines.
  const movable = Math.max(holds.equipmentUnits - holds.startedUnits, 0)
  const parts: string[] = []
  if (movable > 0) parts.push(`${movable} ${movable === 1 ? 'machine' : 'machines'}`)
  if (holds.materialLines > 0) {
    parts.push(`${holds.materialLines} material ${holds.materialLines === 1 ? 'line' : 'lines'}`)
  }

  const started =
    holds.startedUnits > 0
      ? ` ${holds.startedUnits} ${holds.startedUnits === 1 ? 'unit has' : 'units have'} already been started at the bench and will stay on the time ${holds.startedUnits === 1 ? 'it was' : 'they were'} booked for.`
      : ''

  if (parts.length === 0) {
    return started
      ? `Nothing else is held for this run yet.${started}`
      : 'Nothing is held for this run yet, so only the date changes.'
  }

  const list = parts.join(' and ')
  // Clearing is NOT a move: there is no new time to re-take against. Material
  // goes back (the server releases schedule holds on any schedule change), but
  // the machines keep their windows until someone releases them — saying
  // otherwise would promise a cleanup that does not happen.
  return clearing
    ? `This run holds ${list}. Clearing the schedule hands the material back; the machines stay booked until you release them.${started}`
    : `This run holds ${list} for the old time. Confirming moves ${parts.length > 1 ? 'them' : 'it'} to the new one — nothing is double-booked.${started}`
}
